import React, { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react'
import { Box, Typography, TextField, Chip, CircularProgress, Dialog, IconButton, Divider } from '@mui/material'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import Select from 'react-select'
import { useSearchParams } from 'react-router-dom'
import { formatDistanceToNow, subDays, startOfDay, format } from 'date-fns'
import { Plus, X, ExternalLink, Activity, Send, Link2, Check, Lock, List, ListOrdered } from 'lucide-react'
import SearchInput from '../../components/SearchInput'
import CustomAddModal from '../Common/CustomAddModal'
import darkSelectStyles from '../../theme/reactSelectStyles'
import { getTrackerTicketsAdmin, createTrackerTicketAdmin, updateTrackerTicketStatusAdmin, getTrackerActivityLogAdmin, getTrackerCommentsAdmin, addTrackerCommentAdmin, updateTrackerTicketDetailsAdmin, getTrackerTicketByIdAdmin } from '../Common/actions'
import { getSubAdminsListAdmin } from '../LabelRequests/actions'
import { getUpcomingLeaves } from '../Profile/actions'
import { toastSuccess, toastError, getTokenPayload } from '../../utils'
import { SHARE_BASE_URL } from '../../constants'
import FullScreenLoader from '../Common/FullScreenLoader'

const PRIVILEGED_SUB_ADMIN_IDS = [2, 3]  // Vijay Kumar, Harjaspreet Singh
const PRIVILEGED_ASSIGNEE_ID = 3          // Harjaspreet Singh

function renderLinkedText(text) {
    if (!text) return text;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    return parts.map((part, i) =>
        urlRegex.test(part)
            ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ color: '#6C63FF', textDecoration: 'underline', wordBreak: 'break-all' }}>{part}</a>
            : part
    );
}

const REQUEST_TYPE_LABELS = {
    audio_release: 'Audio Release',
    video_release: 'Video Release',
    report_request: 'Report Request',
    withdraw_request: 'Withdraw Request',
    ugc_claim: 'UGC Claim',
    whitelist: 'Whitelist',
    profile_linking: 'Profile Linking',
    distribute_lyrics: 'Lyrics Distribution',
    playlist_pitch: 'Playlist Pitch',
    manual: 'Internal',
}

const REQUEST_TYPE_OPTIONS = [
    { value: '', label: 'All Types' },
    { value: 'audio_release', label: 'Audio Release' },
    { value: 'video_release', label: 'Video Release' },
    { value: 'report_request', label: 'Report Request' },
    { value: 'withdraw_request', label: 'Withdraw Request' },
    { value: 'ugc_claim', label: 'UGC Claim' },
    { value: 'whitelist', label: 'Whitelist' },
    { value: 'profile_linking', label: 'Profile Linking' },
    { value: 'distribute_lyrics', label: 'Lyrics Distribution' },
    { value: 'playlist_pitch', label: 'Playlist Pitch' },
    { value: 'manual', label: 'Internal' },
]

const STATUS_OPTIONS = [
    { value: 'open', label: 'Open' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'done', label: 'Done' },
    { value: 'unassigned', label: 'Unassigned' },
]

const PRIORITY_OPTIONS = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
]

const COLUMNS = [
    { id: 'open', title: 'OPEN', color: '#F59E0B' },
    { id: 'in_progress', title: 'IN PROGRESS', color: '#3B82F6' },
    { id: 'done', title: 'DONE', color: '#10B981' },
]

const PRIORITY_COLORS = {
    low: '#10B981',
    medium: '#F59E0B',
    high: '#EF4444',
}

const STATUS_COLORS = {
    open: '#F59E0B',
    in_progress: '#3B82F6',
    done: '#10B981',
    resolved: '#10B981',
    closed: '#6B7280',
}

const CARD_BG_COLORS = {
    open: '#1E1C15',
    in_progress: '#151A24',
    done: '#131F1A',
}

const CARD_BORDER_COLORS = {
    open: '#3D3520',
    in_progress: '#1E2D45',
    done: '#1A3A2D',
}

const REQUEST_TYPE_ROUTES = {
    audio_release: (id) => `/view_releases/${id}`,
    video_release: (id) => `/view_video_release/${id}`,
    report_request: () => `/report_requests`,
    withdraw_request: () => `/payout`,
    ugc_claim: () => `/ugc_claims`,
    whitelist: () => `/whitelisting`,
    profile_linking: () => `/profile_linking`,
    distribute_lyrics: () => `/lyrics_distribution`,
    playlist_pitch: () => `/playlist_pitching`,
}

const ACTION_TYPE_LABELS = {
    created: 'Created this task',
    status_changed: 'Changed status',
    field_changed: 'Updated field',
    comment_added: 'Added a comment',
}

function getCardStatus(status) {
    if (status === 'resolved' || status === 'closed' || status === 'done') return 'done'
    if (status === 'in_progress') return 'in_progress'
    return 'open'
}

function getCardTitle(ticket) {
    if (ticket.title) return ticket.title
    return REQUEST_TYPE_LABELS[ticket.request_type] || 'Untitled'
}

const CardContent = memo(function CardContent({ ticket, showStatusBadge, isDragging, onShareClick, isAccessible = true }) {
    const cardStatus = getCardStatus(ticket.status)
    return (
        <Box sx={{
            background: CARD_BG_COLORS[cardStatus] || '#1A1D27',
            border: '1px solid',
            borderColor: isDragging ? '#6C63FF' : (CARD_BORDER_COLORS[cardStatus] || '#2E3247'),
            borderLeft: `3px solid ${STATUS_COLORS[cardStatus] || '#6B7280'}`,
            borderRadius: '8px',
            p: '14px',
            mb: '10px',
            minWidth: 0,
            overflow: 'hidden',
            cursor: isDragging !== undefined ? 'grab' : 'default',
            transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.2s',
            opacity: !isAccessible ? 0.7 : isDragging ? 0.9 : 1,
            boxShadow: isDragging ? '0 8px 24px rgba(0,0,0,0.4)' : 'none',
            '&:hover': {
                borderColor: CARD_BORDER_COLORS[cardStatus] || '#6C63FF40',
                borderLeft: `3px solid ${STATUS_COLORS[cardStatus] || '#6B7280'}`,
                boxShadow: '0 4px 16px rgba(108,99,255,0.1)',
                transform: 'translateY(-1px)',
            },
        }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: '8px' }}>
                <Typography sx={{
                    fontSize: '0.875rem', fontWeight: 600, color: '#F1F5F9', lineHeight: 1.4, flex: 1, mr: 1,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', wordBreak: 'break-all',
                }}>
                    {getCardTitle(ticket)}
                </Typography>
                {ticket.priority && (
                    <Typography sx={{
                        fontSize: '0.65rem', fontWeight: 700, flexShrink: 0, mt: '2px',
                        color: PRIORITY_COLORS[ticket.priority] || '#6B7280',
                        textTransform: 'uppercase', letterSpacing: '0.03em',
                    }}>
                        {ticket.priority}
                    </Typography>
                )}
            </Box>

            {ticket.description && (
                <Typography sx={{
                    fontSize: '0.75rem', color: '#94A3B8', mb: '10px', lineHeight: 1.5,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%',
                }}>
                    {ticket.description}
                </Typography>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', mb: '8px', flexWrap: 'wrap' }}>
                {ticket.request_type && (
                    <Chip
                        label={REQUEST_TYPE_LABELS[ticket.request_type] || ticket.request_type}
                        size="small"
                        sx={{
                            fontSize: '0.65rem', height: 20,
                            backgroundColor: '#6C63FF20', color: '#A5A0FF', border: '1px solid #6C63FF30',
                        }}
                    />
                )}

                {showStatusBadge && (
                    <Chip
                        label={cardStatus === 'in_progress' ? 'In Progress' : cardStatus.charAt(0).toUpperCase() + cardStatus.slice(1)}
                        size="small"
                        sx={{
                            fontSize: '0.65rem', height: 20, ml: ticket.request_type ? '6px' : 0,
                            backgroundColor: (STATUS_COLORS[cardStatus] || '#6B7280') + '20',
                            color: STATUS_COLORS[cardStatus] || '#6B7280',
                            border: `1px solid ${(STATUS_COLORS[cardStatus] || '#6B7280')}30`,
                        }}
                    />
                )}

                {!isAccessible && (
                    <Chip
                        icon={<Lock size={10} style={{ color: '#F59E0B' }} />}
                        label="Not Your User"
                        size="small"
                        sx={{
                            fontSize: '0.6rem', height: 20, ml: '6px',
                            backgroundColor: '#F59E0B15', color: '#F59E0B',
                            border: '1px solid #F59E0B30',
                        }}
                    />
                )}

                {ticket.user_email && (
                    <Typography sx={{ fontSize: '0.65rem', color: '#94A3B8', ml: 'auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '50%' }}>
                        {ticket.user_email}
                    </Typography>
                )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: '4px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {ticket.assignee_name ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Box sx={{
                                width: 22, height: 22, borderRadius: '50%', background: '#6C63FF',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '0.65rem', fontWeight: 700, color: '#fff',
                            }}>
                                {ticket.assignee_name.charAt(0).toUpperCase()}
                            </Box>
                            <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8' }}>
                                {ticket.assignee_name}
                            </Typography>
                        </Box>
                    ) : (
                        <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8' }}>
                            Unassigned
                        </Typography>
                    )}
                </Box>
                {onShareClick && (
                    <Box
                        onClick={(e) => { e.stopPropagation(); onShareClick(ticket) }}
                        sx={{
                            display: 'flex', alignItems: 'center', gap: '5px',
                            px: '10px', py: '5px', borderRadius: '5px', cursor: 'pointer',
                            fontSize: '0.7rem', fontWeight: 600, color: '#fff',
                            backgroundColor: '#6C63FF',
                            transition: 'background-color 0.2s',
                            '&:hover': { backgroundColor: '#5B54E0' },
                        }}
                    >
                        <Link2 size={11} />
                        Copy Task
                    </Box>
                )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: '6px' }}>
                <Typography sx={{ fontSize: '0.65rem', color: '#94A3B8' }}>
                    {ticket.created_at && <><span style={{ fontWeight: 600 }}>Created:</span> {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}</>}
                </Typography>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#fff' }}>
                    <span style={{ color: '#94A3B8', fontWeight: 600 }}>Task: </span>{ticket.tracker_id}
                </Typography>
            </Box>

        </Box>
    )
})

const DraggableCard = memo(function DraggableCard({ ticket, index, onClick, onShareClick, isAccessible = true, canInteract = true }) {
    return (
        <Draggable draggableId={String(ticket.tracker_id)} index={index} isDragDisabled={!canInteract}>
            {(provided, snapshot) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    onClick={() => { if (!snapshot.isDragging && onClick) onClick(ticket) }}
                    style={{ ...provided.draggableProps.style, cursor: canInteract ? 'grab' : 'default' }}
                >
                    <CardContent ticket={ticket} showStatusBadge={false} isDragging={snapshot.isDragging} onShareClick={onShareClick} isAccessible={isAccessible} />
                </div>
            )}
        </Draggable>
    )
})

const KanbanColumn = memo(function KanbanColumn({ column, tickets, isLast, onCardClick, onShareClick, hasUserAccess, canInteract }) {
    return (
        <Box sx={{
            flex: 1, width: 0, overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
            borderRight: isLast ? 'none' : '1px solid #1E2130',
        }}>
            <Droppable droppableId={column.id}>
                {(provided, snapshot) => (
                    <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{
                            flex: 1, p: '0 12px 12px', minHeight: 400,
                            transition: 'background 0.2s',
                            background: snapshot.isDraggingOver ? '#1A1D2A' : 'transparent',
                        }}
                    >
                        {tickets.map((ticket, index) => (
                            <DraggableCard key={ticket.tracker_id} ticket={ticket} index={index} onClick={onCardClick} onShareClick={onShareClick} isAccessible={hasUserAccess ? hasUserAccess(ticket) : true} canInteract={canInteract ? canInteract(ticket) : true} />
                        ))}
                        {provided.placeholder}
                    </Box>
                )}
            </Droppable>
        </Box>
    )
})

function TicketDetailModal({ open, ticket, onClose, subAdmins, onTicketUpdated, currentSubAdminId, isPrivilegedSubAdmin, hasUserAccess, adminsOnLeaveToday }) {
    const [activityTab, setActivityTab] = useState('comments')
    const [activityLog, setActivityLog] = useState([])
    const [loadingActivity, setLoadingActivity] = useState(false)
    const [comments, setComments] = useState([])
    const [loadingComments, setLoadingComments] = useState(false)
    const [newComment, setNewComment] = useState('')
    const [addingComment, setAddingComment] = useState(false)
    const [editPriority, setEditPriority] = useState(null)
    const [editAssignee, setEditAssignee] = useState(null)
    const [editStatus, setEditStatus] = useState(null)
    const [saving, setSaving] = useState(false)
    const [copied, setCopied] = useState(false)

    const reversedComments = useMemo(() => [...comments].reverse(), [comments])

    const handleCopyLink = () => {
        const url = `${SHARE_BASE_URL}/?task=${ticket.tracker_id}`
        navigator.clipboard.writeText(url).then(() => {
            setCopied(true)
            toastSuccess('Link copied!')
            setTimeout(() => setCopied(false), 2000)
        })
    }

    const portalSelectStyles = {
        ...darkSelectStyles,
        menuPortal: (base) => ({ ...base, zIndex: 1500 }),
    }

    useEffect(() => {
        if (!ticket) return
        setEditPriority(ticket.priority ? { value: ticket.priority, label: ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1) } : null)
        const assigneeOption = subAdmins.find(sa => sa.value === ticket.assigned_to)
        setEditAssignee(assigneeOption || null)
        const statusOption = STATUS_OPTIONS.find(s => s.value === getCardStatus(ticket.status))
        setEditStatus(statusOption || STATUS_OPTIONS[0])
        setActivityTab('comments')
    }, [ticket, subAdmins])

    useEffect(() => {
        if (!open || !ticket) return
        setLoadingComments(true)
        getTrackerCommentsAdmin(ticket.tracker_id)
            .then(res => { if (res.status === 200) setComments(res.body || []) })
            .catch(() => {})
            .finally(() => setLoadingComments(false))
        setLoadingActivity(true)
        getTrackerActivityLogAdmin(ticket.tracker_id)
            .then(res => { if (res.status === 200) setActivityLog(res.body || []) })
            .catch(() => {})
            .finally(() => setLoadingActivity(false))
    }, [open, ticket])

    const handleAddComment = () => {
        if (!newComment.trim() || addingComment) return
        setAddingComment(true)
        addTrackerCommentAdmin({ tracker_id: ticket.tracker_id, note: newComment.trim() })
            .then(res => {
                if (res.status === 200) {
                    setNewComment('')
                    getTrackerCommentsAdmin(ticket.tracker_id)
                        .then(r => { if (r.status === 200) setComments(r.body || []) })
                        .catch(() => {})
                    getTrackerActivityLogAdmin(ticket.tracker_id)
                        .then(r => { if (r.status === 200) setActivityLog(r.body || []) })
                        .catch(() => {})
                }
            })
            .catch(() => toastError('Failed to add comment'))
            .finally(() => setAddingComment(false))
    }

    const handleFieldSave = (field, value) => {
        setSaving(true)
        const data = { tracker_id: ticket.tracker_id, [field]: value }
        updateTrackerTicketDetailsAdmin(data)
            .then(res => {
                if (res.status === 200) {
                    toastSuccess('Updated')
                    const updatedTicket = { ...ticket }
                    if (field === 'priority') updatedTicket.priority = value
                    if (field === 'assigned_to') {
                        updatedTicket.assigned_to = value
                        const sa = subAdmins.find(s => s.value === value)
                        updatedTicket.assignee_name = sa ? sa.label : null
                    }
                    onTicketUpdated(updatedTicket)
                    getTrackerActivityLogAdmin(ticket.tracker_id)
                        .then(r => { if (r.status === 200) setActivityLog(r.body || []) })
                        .catch(() => {})
                } else {
                    toastError(res.message || 'Failed to update')
                }
            })
            .catch(() => toastError('Failed to update'))
            .finally(() => setSaving(false))
    }

    const handleStatusChange = (val) => {
        if (!val || val.value === getCardStatus(ticket.status)) return
        setEditStatus(val)
        setSaving(true)
        updateTrackerTicketStatusAdmin({ tracker_id: ticket.tracker_id, status: val.value })
            .then(res => {
                if (res.status === 200) {
                    toastSuccess('Status updated')
                    const updatedTicket = { ...ticket, status: val.value, updated_at: new Date().toISOString() }
                    onTicketUpdated(updatedTicket)
                    getTrackerActivityLogAdmin(ticket.tracker_id)
                        .then(r => { if (r.status === 200) setActivityLog(r.body || []) })
                        .catch(() => {})
                } else {
                    toastError(res.message || 'Failed to update status')
                    const oldOption = STATUS_OPTIONS.find(s => s.value === getCardStatus(ticket.status))
                    setEditStatus(oldOption || STATUS_OPTIONS[0])
                }
            })
            .catch(() => {
                toastError('Failed to update status')
                const oldOption = STATUS_OPTIONS.find(s => s.value === getCardStatus(ticket.status))
                setEditStatus(oldOption || STATUS_OPTIONS[0])
            })
            .finally(() => setSaving(false))
    }

    const handleOpenRequest = () => {
        if (!ticket || ticket.request_type === 'manual') return
        const routeFn = REQUEST_TYPE_ROUTES[ticket.request_type]
        if (routeFn) {
            window.open(routeFn(ticket.request_id), '_blank')
        }
    }

    if (!ticket) return null

    const cardStatus = getCardStatus(ticket.status)

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    background: '#161820',
                    border: '1px solid #2E3247',
                    borderRadius: '8px',
                    color: '#F1F5F9',
                    height: '78vh',
                    maxHeight: '78vh',
                    maxWidth: '900px',
                }
            }}
        >
            {/* Two-column body */}
            <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', px: '28px', pt: '20px', pb: '24px', gap: '1px' }}>

                {/* LEFT — Main content */}
                <Box sx={{ flex: '1 1 62%', display: 'flex', flexDirection: 'column', overflow: 'hidden', pr: '24px', mr: '16px' }}>

                    {/* Request Type */}
                    {ticket.request_type && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '16px', mt: '4px' }}>
                            <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#A5A0FF' }}>
                                {REQUEST_TYPE_LABELS[ticket.request_type] || ticket.request_type}
                            </Typography>
                            {ticket.request_type !== 'manual' && (
                                <Box
                                    onClick={handleOpenRequest}
                                    sx={{
                                        display: 'inline-flex', alignItems: 'center', gap: '5px', cursor: 'pointer',
                                        fontSize: '0.8rem', color: '#6C63FF', fontWeight: 600,
                                        border: '1px solid #6C63FF40', borderRadius: '5px',
                                        px: '10px', py: '3px',
                                        '&:hover': { backgroundColor: '#6C63FF15', borderColor: '#6C63FF' },
                                    }}
                                >
                                    <ExternalLink size={14} />
                                    Open Request
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Dates */}
                    <Box sx={{ display: 'flex', gap: 2, mb: '12px' }}>
                        {ticket.created_at && (
                            <Typography sx={{ fontSize: '0.65rem', color: '#CBD5E1' }}>
                                Created {format(new Date(ticket.created_at), 'MMM d, yyyy h:mm a')}
                            </Typography>
                        )}
                        {ticket.updated_at && (
                            <Typography sx={{ fontSize: '0.65rem', color: '#CBD5E1' }}>
                                Updated {format(new Date(ticket.updated_at), 'MMM d, yyyy h:mm a')}
                            </Typography>
                        )}
                    </Box>

                    {/* Title */}
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', mb: '8px' }}>
                        Title
                    </Typography>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#F1F5F9', mb: '24px', lineHeight: 1.4 }}>
                        {getCardTitle(ticket)}
                    </Typography>

                    {/* Description */}
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', mb: '8px' }}>
                        Description
                    </Typography>
                    <Box sx={{ maxHeight: 150, overflowY: 'auto', mb: '28px', '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { background: '#2E3247', borderRadius: '4px' } }}>
                        <Typography sx={{ fontSize: '0.85rem', color: '#CBD5E1', lineHeight: 1.7, whiteSpace: 'pre-wrap', minHeight: 40 }}>
                            {ticket.description ? renderLinkedText(ticket.description) : 'No description provided.'}
                        </Typography>
                    </Box>

                    <Divider sx={{ borderColor: '#1E2235', mb: '20px', flexShrink: 0 }} />

                    {/* Activity section */}
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', mb: '12px', flexShrink: 0 }}>
                        Activity
                    </Typography>
                    <Box sx={{ display: 'flex', gap: '6px', mb: '16px', flexShrink: 0 }}>
                        {['comments', 'history'].map(tab => (
                            <Chip
                                key={tab}
                                label={tab === 'comments' ? 'Comments' : 'History'}
                                size="small"
                                onClick={() => setActivityTab(tab)}
                                sx={{
                                    fontSize: '0.75rem', height: 28, cursor: 'pointer', fontWeight: 600,
                                    backgroundColor: activityTab === tab ? '#6C63FF' : '#1E2235',
                                    color: activityTab === tab ? '#fff' : '#94A3B8',
                                    border: `1px solid ${activityTab === tab ? '#6C63FF' : '#2E3247'}`,
                                    '&:hover': { backgroundColor: activityTab === tab ? '#5B54E0' : '#252940' },
                                }}
                            />
                        ))}
                    </Box>

                    {/* Scrollable activity content */}
                    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>

                    {/* Comments */}
                    {activityTab === 'comments' && (
                        <Box>
                            {/* Comment input */}
                            <Box sx={{ display: 'flex', gap: '10px', mb: '20px' }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Add a comment..."
                                    value={newComment}
                                    onChange={e => setNewComment(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment() } }}
                                    multiline
                                    maxRows={4}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#F1F5F9', fontSize: '0.8rem', background: '#1A1D27', borderRadius: '6px',
                                            '& fieldset': { borderColor: '#2E3247' },
                                            '&:hover fieldset': { borderColor: '#4A5068' },
                                            '&.Mui-focused fieldset': { borderColor: '#6C63FF' },
                                        },
                                    }}
                                />
                                <IconButton
                                    onClick={handleAddComment}
                                    disabled={!newComment.trim() || addingComment}
                                    sx={{ color: '#6C63FF', alignSelf: 'flex-end', '&:disabled': { color: '#2E3247' } }}
                                >
                                    <Send size={18} />
                                </IconButton>
                            </Box>

                            {loadingComments ? (
                                <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={22} sx={{ color: '#6C63FF' }} /></Box>
                            ) : comments.length === 0 ? (
                                <Typography sx={{ color: '#4A5068', fontSize: '0.8rem', py: 2 }}>No comments yet</Typography>
                            ) : (
                                reversedComments.map((c, idx) => (
                                    <Box key={idx} sx={{ display: 'flex', gap: '10px', mb: '16px' }}>
                                        <Box sx={{
                                            width: 30, height: 30, borderRadius: '50%', background: '#6C63FF', flexShrink: 0,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '0.7rem', fontWeight: 700, color: '#fff', mt: '2px',
                                        }}>
                                            {(c.added_by_name || '?').charAt(0).toUpperCase()}
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#F1F5F9' }}>
                                                    {c.added_by_name || 'Unknown'}
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.65rem', color: '#4A5068' }}>
                                                    {c.created_at ? format(new Date(c.created_at), 'MMM d, yyyy h:mm a') : ''}
                                                </Typography>
                                            </Box>
                                            <Typography sx={{ fontSize: '0.8rem', color: '#CBD5E1', mt: '4px', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                                                {renderLinkedText(c.note)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))
                            )}
                        </Box>
                    )}

                    {/* History */}
                    {activityTab === 'history' && (
                        <Box>
                            {loadingActivity ? (
                                <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={22} sx={{ color: '#6C63FF' }} /></Box>
                            ) : activityLog.length === 0 ? (
                                <Typography sx={{ color: '#4A5068', fontSize: '0.8rem', py: 2 }}>No activity yet</Typography>
                            ) : (
                                activityLog.map((entry, idx) => {
                                    const actorName = entry.action_by_name || 'System'
                                    let description = ''
                                    if (entry.action_type === 'created') {
                                        description = 'created this task'
                                    } else if (entry.action_type === 'comment_added') {
                                        description = 'added a comment'
                                    } else if (entry.action_type === 'status_changed') {
                                        const oldLabel = entry.old_value === 'in_progress' ? 'In Progress' : (entry.old_value || '').charAt(0).toUpperCase() + (entry.old_value || '').slice(1)
                                        const newLabel = entry.new_value === 'in_progress' ? 'In Progress' : (entry.new_value || '').charAt(0).toUpperCase() + (entry.new_value || '').slice(1)
                                        description = `changed status from ${oldLabel} to ${newLabel}`
                                    } else if (entry.action_type === 'field_changed' && (entry.field_changed === 'assignee' || entry.field_changed === 'assigned_to')) {
                                        // Resolve names: new entries store names directly (field_changed='assignee'),
                                        // legacy entries store IDs (field_changed='assigned_to') — resolve from subAdmins
                                        let oldName = entry.old_value || 'Unassigned'
                                        let newName = entry.new_value || 'Unassigned'
                                        if (entry.field_changed === 'assigned_to') {
                                            const oldId = parseInt(entry.old_value)
                                            const newId = parseInt(entry.new_value)
                                            oldName = (!entry.old_value || entry.old_value === '0') ? 'Unassigned' : (subAdmins.find(s => s.value === oldId)?.label || entry.old_value)
                                            newName = (!entry.new_value || entry.new_value === '0') ? 'Unassigned' : (subAdmins.find(s => s.value === newId)?.label || entry.new_value)
                                        }
                                        if (newName === 'Unassigned') {
                                            description = `unassigned the task (was ${oldName})`
                                        } else if (oldName === 'Unassigned') {
                                            description = `assigned the task to ${newName}`
                                        } else {
                                            description = `reassigned the task from ${oldName} to ${newName}`
                                        }
                                    } else if (entry.action_type === 'field_changed' && entry.field_changed === 'priority') {
                                        const oldP = (entry.old_value || 'None').charAt(0).toUpperCase() + (entry.old_value || 'None').slice(1)
                                        const newP = (entry.new_value || 'None').charAt(0).toUpperCase() + (entry.new_value || 'None').slice(1)
                                        description = `changed priority from ${oldP} to ${newP}`
                                    } else if (entry.action_type === 'field_changed') {
                                        description = `updated ${entry.field_changed || 'a field'} from "${entry.old_value}" to "${entry.new_value}"`
                                    } else {
                                        description = entry.action_type
                                    }

                                    return (
                                        <Box key={idx} sx={{ display: 'flex', gap: '10px', mb: '14px' }}>
                                            <Box sx={{
                                                width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                                                background: entry.action_type === 'status_changed' ? '#3B82F620' : entry.action_type === 'comment_added' ? '#10B98120' : '#6C63FF20',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', mt: '2px',
                                            }}>
                                                <Activity size={14} color={entry.action_type === 'status_changed' ? '#3B82F6' : entry.action_type === 'comment_added' ? '#10B981' : '#6C63FF'} />
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography sx={{ fontSize: '0.8rem', color: '#94A3B8', lineHeight: 1.5 }}>
                                                    <span style={{ fontWeight: 600, color: '#F1F5F9' }}>{actorName}</span>
                                                    {' '}{description}
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.65rem', color: '#4A5068', mt: '2px' }}>
                                                    {entry.created_at ? format(new Date(entry.created_at), 'MMM d, yyyy h:mm a') : ''}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )
                                })
                            )}
                        </Box>
                    )}
                    </Box>{/* end activity scroll wrapper */}
                </Box>

                {/* RIGHT — Details sidebar */}
                <Box sx={{ flex: '0 0 300px', overflowY: 'auto', background: '#1E2130', borderRadius: '10px 10px 10px 10px', p: '20px', ml: '0px', position: 'relative' }}>
                    {/* Close button — nestled in sidebar top-right */}
                    <IconButton
                        onClick={onClose}
                        sx={{
                            position: 'absolute', top: 0, right: 0,
                            color: '#fff', backgroundColor: '#EF4444',
                            borderRadius: '0 10px 0 10px',
                            width: 36, height: 36,
                            '&:hover': { backgroundColor: '#DC2626' },
                        }}
                    >
                        <X size={16} />
                    </IconButton>

                    {/* Status */}
                    <Box sx={{ mb: '18px' }}>
                        <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', mb: '6px', fontWeight: 600 }}>Status</Typography>
                        <Select
                            value={editStatus}
                            onChange={handleStatusChange}
                            options={STATUS_OPTIONS}
                            formatOptionLabel={(opt) => (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{
                                        width: 10, height: 10, borderRadius: '50%', display: 'inline-block',
                                        background: STATUS_COLORS[opt.value] || '#6B7280',
                                    }} />
                                    <span>{opt.label}</span>
                                </div>
                            )}
                            styles={{
                                ...portalSelectStyles,
                                control: (base, state) => ({
                                    ...base,
                                    backgroundColor: '#282C3E',
                                    borderColor: state.isFocused ? '#6C63FF' : '#363B52',
                                    color: '#F1F5F9',
                                    boxShadow: state.isFocused ? '0 0 0 1px #6C63FF' : 'none',
                                    '&:hover': { borderColor: '#6C63FF' },
                                    minHeight: 36,
                                    borderRadius: 6,
                                }),
                                menu: (base) => ({ ...base, backgroundColor: '#282C3E', border: '1px solid #363B52', zIndex: 9999 }),
                                menuList: (base) => ({ ...base, backgroundColor: '#282C3E' }),
                                option: (base, state) => ({
                                    ...base,
                                    backgroundColor: state.isSelected ? '#6C63FF' : state.isFocused ? '#363B52' : '#282C3E',
                                    color: '#F1F5F9',
                                    '&:active': { backgroundColor: '#6C63FF' },
                                }),
                            }}
                            menuPortalTarget={document.body}
                            isDisabled={saving}
                            isSearchable={false}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '10px' }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                            Details
                        </Typography>
                        {saving && <CircularProgress size={14} sx={{ color: '#6C63FF' }} />}
                    </Box>

                    {/* Assignee */}
                    <Box sx={{ mb: '18px' }}>
                        <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', mb: '6px', fontWeight: 600 }}>Assignee</Typography>
                        <Select
                            value={editAssignee}
                            onChange={(val) => {
                                setEditAssignee(val)
                                handleFieldSave('assigned_to', val?.value || null)
                            }}
                            options={subAdmins}
                            isOptionDisabled={(option) => {
                                if (adminsOnLeaveToday && adminsOnLeaveToday.has(option.value)) return true
                                if (ticket.request_type === 'manual') return false
                                if (isPrivilegedSubAdmin && hasUserAccess && !hasUserAccess(ticket)) {
                                    return option.value !== PRIVILEGED_ASSIGNEE_ID
                                }
                                if (!option.usersAccess) return false
                                if (!option.usersAccess.length) return true
                                return !option.usersAccess.includes(Number(ticket.user_id))
                            }}
                            formatOptionLabel={(option) => adminsOnLeaveToday && adminsOnLeaveToday.has(option.value)
                                ? <span>{option.label} <span style={{ color: '#EF4444', fontSize: '0.75em' }}>(On Leave)</span></span>
                                : option.label
                            }
                            styles={{
                                ...portalSelectStyles,
                                control: (base, state) => ({
                                    ...base,
                                    backgroundColor: '#282C3E',
                                    borderColor: state.isFocused ? '#6C63FF' : '#363B52',
                                    color: '#F1F5F9',
                                    boxShadow: state.isFocused ? '0 0 0 1px #6C63FF' : 'none',
                                    '&:hover': { borderColor: '#6C63FF' },
                                    minHeight: 36,
                                    borderRadius: 6,
                                }),
                                menu: (base) => ({ ...base, backgroundColor: '#282C3E', border: '1px solid #363B52', zIndex: 9999 }),
                                menuList: (base) => ({ ...base, backgroundColor: '#282C3E' }),
                                option: (base, state) => ({
                                    ...base,
                                    backgroundColor: state.isSelected ? '#6C63FF' : state.isFocused ? '#363B52' : '#282C3E',
                                    color: state.isDisabled ? '#4A5068' : '#F1F5F9',
                                    '&:active': { backgroundColor: state.isDisabled ? '#282C3E' : '#6C63FF' },
                                }),
                            }}
                            menuPortalTarget={document.body}
                            placeholder="Unassigned"
                            isClearable
                            isDisabled={saving}
                        />
                    </Box>

                    {/* Priority */}
                    <Box sx={{ mb: '18px' }}>
                        <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', mb: '6px', fontWeight: 600 }}>Priority</Typography>
                        <Select
                            value={editPriority}
                            onChange={(val) => {
                                setEditPriority(val)
                                handleFieldSave('priority', val?.value || null)
                            }}
                            options={PRIORITY_OPTIONS}
                            formatOptionLabel={(opt) => (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{
                                        width: 10, height: 10, borderRadius: '50%', display: 'inline-block',
                                        background: PRIORITY_COLORS[opt.value] || '#6B7280',
                                    }} />
                                    <span>{opt.label}</span>
                                </div>
                            )}
                            styles={{
                                ...portalSelectStyles,
                                control: (base, state) => ({
                                    ...base,
                                    backgroundColor: '#282C3E',
                                    borderColor: state.isFocused ? '#6C63FF' : '#363B52',
                                    color: '#F1F5F9',
                                    boxShadow: state.isFocused ? '0 0 0 1px #6C63FF' : 'none',
                                    '&:hover': { borderColor: '#6C63FF' },
                                    minHeight: 36,
                                    borderRadius: 6,
                                }),
                                menu: (base) => ({ ...base, backgroundColor: '#282C3E', border: '1px solid #363B52', zIndex: 9999 }),
                                menuList: (base) => ({ ...base, backgroundColor: '#282C3E' }),
                                option: (base, state) => ({
                                    ...base,
                                    backgroundColor: state.isSelected ? '#6C63FF' : state.isFocused ? '#363B52' : '#282C3E',
                                    color: '#F1F5F9',
                                    '&:active': { backgroundColor: '#6C63FF' },
                                }),
                            }}
                            menuPortalTarget={document.body}
                            placeholder="None"
                            isClearable
                            isDisabled={saving}
                        />
                    </Box>

                    {/* User info */}
                    {ticket.user_name && (
                        <Box sx={{ mb: '18px' }}>
                            <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', mb: '6px', fontWeight: 600 }}>User</Typography>
                            <Typography sx={{ fontSize: '0.8rem', color: '#fff', fontWeight: 500 }}>
                                {ticket.user_name}
                            </Typography>
                            {ticket.user_email && (
                                <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', mt: '2px' }}>
                                    {ticket.user_email}
                                </Typography>
                            )}
                        </Box>
                    )}

                    <Divider sx={{ borderColor: '#363B52', my: '18px' }} />

                    {/* Copy Ticket Link button */}
                    <Box
                        onClick={handleCopyLink}
                        sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                            width: '100%', py: '10px', borderRadius: '8px', cursor: 'pointer',
                            fontWeight: 700, fontSize: '0.8rem',
                            color: '#fff',
                            backgroundColor: copied ? '#10B981' : '#6C63FF',
                            transition: 'background-color 0.2s',
                            '&:hover': { backgroundColor: copied ? '#059669' : '#5B54E0' },
                        }}
                    >
                        {copied ? <Check size={16} /> : <Link2 size={16} />}
                        {copied ? 'Copied!' : 'Copy Task Link'}
                    </Box>

                    {/* Ticket Number */}
                    <Box sx={{ mt: '16px', textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '0.8rem', color: '#94A3B8', fontWeight: 600, mb: '4px' }}>
                            Task Number
                        </Typography>
                        <Typography sx={{ fontSize: '3rem', fontWeight: 800, color: '#F1F5F9', lineHeight: 1 }}>
                            {ticket.tracker_id}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Dialog>
    )
}

export default function Tracker() {
    const [tickets, setTickets] = useState([])
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState(null)
    const [priorityFilter, setPriorityFilter] = useState(null)
    const [typeFilter, setTypeFilter] = useState(null)
    const [myTickets, setMyTickets] = useState(false)
    const [assignedToFilter, setAssignedToFilter] = useState(null)
    const [createModalOpen, setCreateModalOpen] = useState(false)
    const [newTitle, setNewTitle] = useState('')
    const [newDescription, setNewDescription] = useState('')
    const [newPriority, setNewPriority] = useState({ value: 'medium', label: 'Medium' })
    const [newAssignee, setNewAssignee] = useState(null)
    const descTextareaRef = useRef(null)
    const [subAdmins, setSubAdmins] = useState([])
    const [adminsOnLeaveToday, setAdminsOnLeaveToday] = useState(new Set())
    const [creating, setCreating] = useState(false)
    const [detailModalOpen, setDetailModalOpen] = useState(false)
    const [selectedTicket, setSelectedTicket] = useState(null)
    const [searchParams, setSearchParams] = useSearchParams()
    const autoOpenHandled = useRef(false)

    const openDetailModal = (ticket) => {
        if (!canInteract(ticket)) return
        setSelectedTicket(ticket)
        setDetailModalOpen(true)
        setSearchParams({ task: ticket.tracker_id }, { replace: true })
    }

    const closeDetailModal = () => {
        setDetailModalOpen(false)
        setSelectedTicket(null)
        setSearchParams({}, { replace: true })
    }

    const handleShareClick = (ticket) => {
        const url = `${SHARE_BASE_URL}/?task=${ticket.tracker_id}`
        navigator.clipboard.writeText(url).then(() => {
            toastSuccess('Link copied!')
        })
    }

    const handleTicketUpdated = (updatedTicket) => {
        setSelectedTicket(updatedTicket)
        setTickets(prev => prev.map(t => t.tracker_id === updatedTicket.tracker_id ? { ...t, ...updatedTicket } : t))
        fetchTickets()
    }

    const tokenPayload = getTokenPayload()
    const currentSubAdminId = tokenPayload?.subAdminId

    const usersAccessList = (() => {
        try { return tokenPayload?.usersAccess ? JSON.parse(tokenPayload.usersAccess) : [] }
        catch { return [] }
    })()

    const hasUserAccess = (ticket) => {
        if (!usersAccessList.length) return true
        if (!ticket.user_id) return true
        return usersAccessList.includes(ticket.user_id)
    }

    const isPrivilegedSubAdmin = PRIVILEGED_SUB_ADMIN_IDS.includes(currentSubAdminId)

    const canInteract = (ticket) => {
        return hasUserAccess(ticket) || isPrivilegedSubAdmin
    }

    const isFiltered = search || (statusFilter && statusFilter.value) || (priorityFilter && priorityFilter.value) || (typeFilter && typeFilter.value) || myTickets || assignedToFilter

    const fetchTickets = useCallback(() => {
        const filters = {}
        if (statusFilter?.value && statusFilter.value !== 'unassigned') filters.status = statusFilter.value
        if (priorityFilter?.value) filters.priority = priorityFilter.value
        if (typeFilter?.value) filters.request_type = typeFilter.value
        if (assignedToFilter?.value) filters.assigned_to = assignedToFilter.value
        else if (myTickets && currentSubAdminId) filters.assigned_to = currentSubAdminId
        if (search) filters.search = search
        filters.sort_by = 'date_desc'

        getTrackerTicketsAdmin(filters)
            .then(res => {
                if (res.status === 200) {
                    let result = res.body || []
                    if (statusFilter?.value === 'unassigned') {
                        result = result.filter(t => !t.assigned_to)
                    }
                    setTickets(result)
                }
            })
            .catch(() => toastError('Failed to load tasks'))
            .finally(() => setLoading(false))
    }, [statusFilter, priorityFilter, typeFilter, myTickets, assignedToFilter, search, currentSubAdminId])

    useEffect(() => {
        document.title = 'TS | Task';
        return () => { document.title = 'Fronic Media Admin'; };
    }, []);

    useEffect(() => {
        fetchTickets()
    }, [fetchTickets])

    // Auto-refresh every 3 minutes (reduced from 1 min for performance)
    useEffect(() => {
        const interval = setInterval(() => {
            fetchTickets()
        }, 180000)
        return () => clearInterval(interval)
    }, [fetchTickets])

    useEffect(() => {
        getSubAdminsListAdmin()
            .then(res => {
                if (res.status === 200) {
                    setSubAdmins((res.body || []).map(sa => ({
                        value: sa.subAdminId,
                        label: sa.name,
                        usersAccess: (() => {
                            try { return sa.usersAccess ? JSON.parse(sa.usersAccess) : [] }
                            catch { return [] }
                        })()
                    })))
                }
            })
            .catch(() => {})

        // Fetch today's approved leaves to disable those admins in assignment dropdowns
        getUpcomingLeaves()
            .then(res => {
                const today = new Date()
                const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
                const onLeaveIds = new Set()
                const shortTypes = ['short_1hr', 'short_1_5hr', 'short_2hr', 'short_2_5hr', 'short_3hr']
                ;(res.body || []).forEach(leave => {
                    if (leave.leaveDate === todayStr && leave.status === 'approved' && !shortTypes.includes(leave.leaveType)) {
                        onLeaveIds.add(leave.subAdminId)
                    }
                })
                setAdminsOnLeaveToday(onLeaveIds)
            })
            .catch(() => {})
    }, [])

    // Auto-open ticket from URL query param (?task=123)
    useEffect(() => {
        if (autoOpenHandled.current || loading) return
        const ticketId = searchParams.get('task')
        if (!ticketId) return

        autoOpenHandled.current = true

        // Try to find in already-loaded tickets first
        const found = tickets.find(t => String(t.tracker_id) === String(ticketId))
        if (found) {
            setSelectedTicket(found)
            setDetailModalOpen(true)
            return
        }

        // Not in current view — fetch from API
        getTrackerTicketByIdAdmin(ticketId)
            .then(res => {
                if (res.status === 200 && res.body) {
                    setSelectedTicket(res.body)
                    setDetailModalOpen(true)
                } else {
                    toastError('Task not found')
                    setSearchParams({}, { replace: true })
                }
            })
            .catch(() => {
                toastError('Failed to load task')
                setSearchParams({}, { replace: true })
            })
    }, [loading, tickets, searchParams, setSearchParams])

    const onDragEnd = useCallback((result) => {
        const { destination, source, draggableId } = result
        if (!destination || destination.droppableId === source.droppableId) return

        const trackerId = parseInt(draggableId)
        const ticket = tickets.find(t => t.tracker_id === trackerId)
        if (ticket && !canInteract(ticket)) return
        const newStatus = destination.droppableId

        setTickets(prev => prev.map(t =>
            t.tracker_id === trackerId ? { ...t, status: newStatus, updated_at: new Date().toISOString() } : t
        ))

        updateTrackerTicketStatusAdmin({ tracker_id: trackerId, status: newStatus })
            .then(res => {
                if (res.status !== 200) {
                    toastError(res.message || 'Failed to update status')
                    fetchTickets()
                }
            })
            .catch(() => {
                toastError('Failed to update status')
                fetchTickets()
            })
    }, [fetchTickets])

    const handleDescKeyDown = (e) => {
        const textarea = descTextareaRef.current;
        if (!textarea) return;
        const { selectionStart, selectionEnd } = textarea;
        if (selectionStart !== selectionEnd) return;
        const value = newDescription;
        const beforeCursor = value.slice(0, selectionStart);
        const lastNewline = beforeCursor.lastIndexOf("\n");
        const currentLine = beforeCursor.slice(lastNewline + 1);

        if (e.key === "Enter") {
            if (currentLine === "\u2022 ") {
                e.preventDefault();
                const newValue = value.slice(0, selectionStart - 2) + value.slice(selectionStart);
                setNewDescription(newValue);
                setTimeout(() => { textarea.selectionStart = textarea.selectionEnd = selectionStart - 2; }, 0);
                return;
            }
            if (currentLine.startsWith("\u2022 ")) {
                e.preventDefault();
                const insert = "\n\u2022 ";
                const newValue = value.slice(0, selectionStart) + insert + value.slice(selectionStart);
                setNewDescription(newValue);
                setTimeout(() => { textarea.selectionStart = textarea.selectionEnd = selectionStart + insert.length; }, 0);
                return;
            }
            const numMatch = currentLine.match(/^(\d+)\.\s$/);
            if (numMatch) {
                e.preventDefault();
                const prefixLen = numMatch[0].length;
                const newValue = value.slice(0, selectionStart - prefixLen) + value.slice(selectionStart);
                setNewDescription(newValue);
                setTimeout(() => { textarea.selectionStart = textarea.selectionEnd = selectionStart - prefixLen; }, 0);
                return;
            }
            const numContentMatch = currentLine.match(/^(\d+)\.\s/);
            if (numContentMatch) {
                e.preventDefault();
                const nextNum = parseInt(numContentMatch[1]) + 1;
                const insert = "\n" + nextNum + ". ";
                const newValue = value.slice(0, selectionStart) + insert + value.slice(selectionStart);
                setNewDescription(newValue);
                setTimeout(() => { textarea.selectionStart = textarea.selectionEnd = selectionStart + insert.length; }, 0);
                return;
            }
        }
    }

    const toggleDescBulletList = () => {
        const textarea = descTextareaRef.current;
        if (!textarea) return;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const lines = newDescription.split("\n");
        let charCount = 0;
        let startLine = 0, endLine = 0;
        for (let i = 0; i < lines.length; i++) {
            const lineEnd = charCount + lines[i].length;
            if (charCount <= start && start <= lineEnd) startLine = i;
            if (charCount <= end && end <= lineEnd) endLine = i;
            charCount += lines[i].length + 1;
        }
        const newLines = lines.map((line, i) => {
            if (i < startLine || i > endLine) return line;
            if (line.startsWith("\u2022 ")) return line.slice(2);
            const numberedMatch = line.match(/^\d+\.\s/);
            if (numberedMatch) return "\u2022 " + line.slice(numberedMatch[0].length);
            return "\u2022 " + line;
        });
        setNewDescription(newLines.join("\n"));
    }

    const toggleDescNumberedList = () => {
        const textarea = descTextareaRef.current;
        if (!textarea) return;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const lines = newDescription.split("\n");
        let charCount = 0;
        let startLine = 0, endLine = 0;
        for (let i = 0; i < lines.length; i++) {
            const lineEnd = charCount + lines[i].length;
            if (charCount <= start && start <= lineEnd) startLine = i;
            if (charCount <= end && end <= lineEnd) endLine = i;
            charCount += lines[i].length + 1;
        }
        let num = 1;
        const newLines = lines.map((line, i) => {
            if (i < startLine || i > endLine) return line;
            const numberedMatch = line.match(/^\d+\.\s/);
            if (numberedMatch) return line.slice(numberedMatch[0].length);
            if (line.startsWith("\u2022 ")) return num++ + ". " + line.slice(2);
            return num++ + ". " + line;
        });
        setNewDescription(newLines.join("\n"));
    }

    const handleCreateTicket = () => {
        if (!newTitle.trim()) return toastError('Title is required')
        setCreating(true)
        createTrackerTicketAdmin({
            title: newTitle.trim(),
            description: newDescription.trim() || undefined,
            priority: newPriority?.value || 'medium',
            assigned_to: newAssignee?.value || undefined,
        })
            .then(res => {
                if (res.status === 200) {
                    toastSuccess('Task created')
                    setCreateModalOpen(false)
                    setNewTitle('')
                    setNewDescription('')
                    setNewPriority({ value: 'medium', label: 'Medium' })
                    setNewAssignee(null)
                    fetchTickets()
                } else {
                    toastError(res.message || 'Failed to create task')
                }
            })
            .catch(() => toastError('Failed to create task'))
            .finally(() => setCreating(false))
    }

    const clearFilters = () => {
        setSearch('')
        setStatusFilter(null)
        setPriorityFilter(null)
        setTypeFilter(null)
        setMyTickets(false)
        setAssignedToFilter(null)
    }

    const openTickets = tickets.filter(t => getCardStatus(t.status) === 'open')
    const inProgressTickets = tickets.filter(t => getCardStatus(t.status) === 'in_progress')
    const yesterdayStart = startOfDay(subDays(new Date(), 1))
    const doneTickets = tickets.filter(t => {
        if (getCardStatus(t.status) !== 'done') return false
        const doneDate = new Date(t.updated_at || t.created_at)
        return doneDate >= yesterdayStart
    }).slice(0, 10)

    const columnData = {
        open: openTickets,
        in_progress: inProgressTickets,
        done: doneTickets,
    }

    return (
        <Box sx={{ p: '0px', mx: '-24px', mt: '-24px' }}>
            <FullScreenLoader open={loading} />
            {/* Sticky Filter Bar + Column Headers */}
            <Box sx={{ position: 'sticky', top: '64px', zIndex: 10, background: '#0F1117' }}>
                {/* Filter Bar */}
                <Box sx={{
                    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px',
                    mb: 0, p: '14px 24px', background: '#13151F', borderBottom: '1px solid #1E2130',
                }}>
                    <Box sx={{ flex: '2 1 220px', minWidth: 180 }}>
                        <SearchInput
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search tasks..."
                        />
                    </Box>
                    <Box sx={{ flex: '1 1 140px', minWidth: 130 }}>
                        <Select
                            value={statusFilter}
                            onChange={setStatusFilter}
                            options={STATUS_OPTIONS}
                            styles={darkSelectStyles}
                            placeholder="Status"
                            isClearable
                        />
                    </Box>
                    <Box sx={{ flex: '1 1 140px', minWidth: 130 }}>
                        <Select
                            value={priorityFilter}
                            onChange={setPriorityFilter}
                            options={PRIORITY_OPTIONS}
                            styles={darkSelectStyles}
                            placeholder="Priority"
                            isClearable
                        />
                    </Box>
                    <Box sx={{ flex: '1 1 150px', minWidth: 130 }}>
                        <Select
                            value={typeFilter}
                            onChange={setTypeFilter}
                            options={REQUEST_TYPE_OPTIONS}
                            styles={darkSelectStyles}
                            placeholder="Type"
                            isClearable
                        />
                    </Box>
                    {currentSubAdminId !== 1 && (
                        <Box sx={{ flex: '1 1 160px', minWidth: 150 }}>
                            <Select
                                options={subAdmins}
                                value={assignedToFilter}
                                onChange={(val) => { setAssignedToFilter(val); if (val) setMyTickets(false) }}
                                placeholder="Assigned to"
                                isClearable
                                styles={darkSelectStyles}
                            />
                        </Box>
                    )}
                    <Chip
                        label="ME"
                        onClick={() => { setMyTickets(!myTickets); if (!myTickets) setAssignedToFilter(null) }}
                        sx={{
                            height: 36, borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                            backgroundColor: myTickets ? '#6C63FF' : '#232636',
                            color: myTickets ? '#fff' : '#94A3B8',
                            border: `1px solid ${myTickets ? '#6C63FF' : '#2E3247'}`,
                            '&:hover': { backgroundColor: myTickets ? '#5B54E0' : '#2A2D3E' },
                        }}
                    />
                    {isFiltered && (
                        <Chip
                            icon={<X size={14} color="#EF4444" />}
                            label="Clear"
                            onClick={clearFilters}
                            sx={{
                                height: 36, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                                backgroundColor: '#EF444420', color: '#EF4444', border: '1px solid #EF444430',
                                '&:hover': { backgroundColor: '#EF444430' },
                            }}
                        />
                    )}
                    <Chip
                        icon={<Plus size={16} color="#fff" />}
                        label="Create"
                        onClick={() => setCreateModalOpen(true)}
                        sx={{
                            height: 36, borderRadius: '6px', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer',
                            backgroundColor: '#10B981', color: '#fff', border: '1px solid #10B981',
                            '&:hover': { backgroundColor: '#059669' },
                        }}
                    />
                </Box>

                {/* Column Headers — only in Kanban mode */}
                {!loading && !isFiltered && (
                    <Box sx={{ display: 'flex', background: '#13151F', borderBottom: '1px solid #1E2130' }}>
                        {COLUMNS.map((col, idx) => (
                            <Box key={col.id} sx={{
                                flex: 1, p: '16px 16px 12px', display: 'flex', alignItems: 'center', gap: '10px',
                                borderRight: idx === COLUMNS.length - 1 ? 'none' : '1px solid #1E2130',
                            }}>
                                <Typography sx={{
                                    fontSize: '0.7rem', fontWeight: 700, color: '#4A5068',
                                    letterSpacing: '0.08em', textTransform: 'uppercase',
                                }}>
                                    {col.title}
                                </Typography>
                                <Box sx={{
                                    background: '#6C63FF', borderRadius: '10px', px: '8px', py: '2px',
                                    fontSize: '0.65rem', fontWeight: 700, color: '#fff', lineHeight: 1.4,
                                }}>
                                    {columnData[col.id].length}
                                </Box>
                                {col.id === 'done' && (
                                    <Typography sx={{ fontWeight: 400, fontSize: '0.6rem', color: '#4A5068' }}>
                                        (Showing tasks of last two days with a limit of 10)
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>

            {/* Loading state */}
            {loading && (
                <Box sx={{ textAlign: 'center', py: 8, px: '24px' }}>
                    <CircularProgress size={30} sx={{ color: '#6C63FF' }} />
                </Box>
            )}

            {/* Kanban Board (default, no filters active) */}
            {!loading && !isFiltered && (
                <DragDropContext onDragEnd={onDragEnd}>
                    <Box sx={{ display: 'flex', alignItems: 'stretch', overflowX: 'auto', minHeight: 'calc(100vh - 140px)' }}>
                        {COLUMNS.map((col, idx) => (
                            <KanbanColumn key={col.id} column={col} tickets={columnData[col.id]} isLast={idx === COLUMNS.length - 1} onCardClick={openDetailModal} onShareClick={handleShareClick} hasUserAccess={hasUserAccess} canInteract={canInteract} />
                        ))}
                    </Box>
                </DragDropContext>
            )}

            {/* Filtered flat list */}
            {!loading && isFiltered && (
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                    gap: '12px',
                    p: '16px 24px',
                }}>
                    {tickets.length === 0 ? (
                        <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 8 }}>
                            <Typography sx={{ color: '#4A5068', fontSize: '0.95rem' }}>No tasks match your filters</Typography>
                        </Box>
                    ) : (
                        tickets.map((ticket) => (
                            <Box key={ticket.tracker_id} onClick={() => openDetailModal(ticket)} sx={{ cursor: canInteract(ticket) ? 'pointer' : 'default' }}>
                                <CardContent ticket={ticket} showStatusBadge={true} onShareClick={handleShareClick} isAccessible={hasUserAccess(ticket)} />
                            </Box>
                        ))
                    )}
                </Box>
            )}

            {/* No tickets at all */}
            {!loading && !isFiltered && tickets.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 8, px: '24px' }}>
                    <Typography sx={{ color: '#4A5068', fontSize: '0.95rem' }}>No tasks yet</Typography>
                </Box>
            )}

            {/* Create Ticket Modal */}
            <CustomAddModal
                open={createModalOpen}
                handleClose={() => setCreateModalOpen(false)}
                title="Create Task"
                saveLabel={creating ? 'Creating...' : 'Create'}
                onSubmitConfirm={handleCreateTicket}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', mt: 1 }}>
                    <TextField
                        label="Title"
                        variant="outlined"
                        size="small"
                        fullWidth
                        required
                        value={newTitle}
                        onChange={e => setNewTitle(e.target.value)}
                    />
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Typography sx={{ fontSize: '0.8rem', color: '#94A3B8', mr: 1 }}>Description</Typography>
                            <Box
                                component="button"
                                onClick={toggleDescBulletList}
                                sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    width: 28, height: 28, border: '1px solid var(--border)',
                                    borderRadius: '6px', backgroundColor: 'rgba(108, 99, 255, 0.15)',
                                    cursor: 'pointer', color: '#6C63FF', transition: 'all 0.2s',
                                    '&:hover': { backgroundColor: 'rgba(108, 99, 255, 0.25)' },
                                }}
                                title="Bullet list"
                            >
                                <List size={14} />
                            </Box>
                            <Box
                                component="button"
                                onClick={toggleDescNumberedList}
                                sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    width: 28, height: 28, border: '1px solid var(--border)',
                                    borderRadius: '6px', backgroundColor: 'rgba(108, 99, 255, 0.15)',
                                    cursor: 'pointer', color: '#6C63FF', transition: 'all 0.2s',
                                    '&:hover': { backgroundColor: 'rgba(108, 99, 255, 0.25)' },
                                }}
                                title="Numbered list"
                            >
                                <ListOrdered size={14} />
                            </Box>
                        </Box>
                        <TextField
                            inputRef={descTextareaRef}
                            variant="outlined"
                            size="small"
                            fullWidth
                            multiline
                            minRows={5}
                            maxRows={12}
                            value={newDescription}
                            onChange={e => setNewDescription(e.target.value)}
                            onKeyDown={handleDescKeyDown}
                            placeholder="Add description..."
                        />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: '0.8rem', color: '#94A3B8', mb: '6px' }}>Priority</Typography>
                        <Select
                            value={newPriority}
                            onChange={setNewPriority}
                            options={PRIORITY_OPTIONS}
                            styles={darkSelectStyles}
                            placeholder="Select priority"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: '0.8rem', color: '#94A3B8', mb: '6px' }}>Assign To</Typography>
                        <Select
                            value={newAssignee}
                            onChange={setNewAssignee}
                            options={subAdmins}
                            styles={darkSelectStyles}
                            placeholder="Select assignee"
                            isClearable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            isOptionDisabled={(option) => adminsOnLeaveToday.has(option.value)}
                            formatOptionLabel={(option) => adminsOnLeaveToday.has(option.value)
                                ? <span>{option.label} <span style={{ color: '#EF4444', fontSize: '0.75em' }}>(On Leave)</span></span>
                                : option.label
                            }
                        />
                    </Box>
                </Box>
            </CustomAddModal>

            {/* Ticket Detail Modal */}
            <TicketDetailModal
                open={detailModalOpen}
                ticket={selectedTicket}
                onClose={closeDetailModal}
                subAdmins={subAdmins}
                onTicketUpdated={handleTicketUpdated}
                currentSubAdminId={currentSubAdminId}
                isPrivilegedSubAdmin={isPrivilegedSubAdmin}
                hasUserAccess={hasUserAccess}
                adminsOnLeaveToday={adminsOnLeaveToday}
            />
        </Box>
    )
}
